<template>
  <!-- Fixed height; width follows the logo's own aspect ratio so wide
       wordmarks stay legible, while initials stay a square tile. -->
  <Avatar
    :class="cn(
      'shrink-0 border',
      sizes.box,
      logoSrc ? cn('w-auto bg-white shadow-sm', sizes.logo) : cn('bg-primary/10', sizes.square),
      props.class,
    )"
  >
    <AvatarImage
      v-if="logoSrc"
      :src="logoSrc"
      :alt="`${workspace?.name ?? 'Workspace'} logo`"
      class="aspect-auto h-full w-auto max-w-full object-contain"
      @error="onLogoError"
    />
    <AvatarFallback
      :class="cn('bg-transparent font-semibold text-primary', sizes.radius, sizes.text)"
    >
      {{ initials }}
    </AvatarFallback>
  </Avatar>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import type { HTMLAttributes } from 'vue'
import { cn } from '@/lib/utils'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  faviconUrlFromWebsite,
  getInitials,
  isPlaceholderAvatarUrl,
  isSharedDefaultAvatarUrl,
  trackAvatarUrl,
} from '@/lib/avatar'

defineOptions({ name: 'WorkspaceLogo' })

type WorkspaceLike = {
  id?: string | number
  name?: string
  avatar_url?: string | null
  website?: string | null
}

const props = withDefaults(defineProps<{
  workspace?: WorkspaceLike | null
  size?: 'md' | 'lg'
  class?: HTMLAttributes['class']
}>(), {
  workspace: null,
  size: 'md',
  class: '',
})

const SIZES = {
  md: {
    box: 'h-14 min-w-14 rounded-xl',
    square: 'w-14',
    logo: 'max-w-32 px-2 py-1.5',
    radius: 'rounded-xl',
    text: 'text-base',
  },
  lg: {
    box: 'h-20 min-w-20 rounded-2xl',
    square: 'w-20',
    logo: 'max-w-48 px-3 py-2',
    radius: 'rounded-2xl',
    text: 'text-2xl',
  },
} as const

const sizes = computed(() => SIZES[props.size])

const initials = computed(() => getInitials(props.workspace?.name ?? '') || 'W')

/** 0 = try upload, 1 = try website favicon, 2 = initials only */
const logoStage = ref(0)

const uploadedLogoSrc = computed(() => {
  const src = props.workspace?.avatar_url?.trim()
  if (!src || isPlaceholderAvatarUrl(src) || isSharedDefaultAvatarUrl(src)) return null
  return src
})

const websiteLogoSrc = computed(() => faviconUrlFromWebsite(props.workspace?.website, 128))

// Prefer an uploaded logo; otherwise pull the site favicon from `website`.
const logoSrc = computed(() => {
  if (logoStage.value === 0 && uploadedLogoSrc.value) return uploadedLogoSrc.value
  if (logoStage.value <= 1 && websiteLogoSrc.value) {
    // Skip straight to favicon when there is no usable upload.
    if (logoStage.value === 0 && !uploadedLogoSrc.value) return websiteLogoSrc.value
    if (logoStage.value === 1) return websiteLogoSrc.value
  }
  return null
})

const onLogoError = () => {
  if (logoStage.value === 0 && uploadedLogoSrc.value && websiteLogoSrc.value) {
    logoStage.value = 1
    return
  }
  logoStage.value = 2
}

// The API returns one stock image for every workspace without an upload.
// Track logos under a `workspace:` key so they never collide with user ids in
// the shared avatar registry; a URL seen on 2+ workspaces is a default.
watch(
  () => [props.workspace?.avatar_url, props.workspace?.id, props.workspace?.website] as const,
  ([src, id]) => {
    logoStage.value = 0
    if (src && id != null) trackAvatarUrl(src, `workspace:${id}`)
  },
  { immediate: true },
)
</script>
